import { Box, Collapse, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals } from '../../../../core-components/theme/color-palette/colors';
import { SwitchInput } from '../../../../core-components/theme/components/Fields';
import { SurfaceCard } from '../../../../core-components/theme/components/SurfaceCard';
import { formCardSx, proseSx } from '../../constants/layout';
import { hhmmToMinute, quietClock } from '../../domain/quietHours';
import type { QuietWindowFailure, QuietWindowField, WindowSummary } from '../../domain/quietHours';
import { useLocale } from '../../hooks/logic/useLocale';

import { explainer, switchLabel } from './quietText';
import { QuietWindowFields } from './QuietWindowFields';

const collapseSx = { '@media (prefers-reduced-motion: reduce)': { transition: 'none !important' } } as const;
const valueSx = { color: Neutrals[900], fontVariantNumeric: 'tabular-nums' } as const;
const labelSx = { color: Neutrals[900] } as const;
const readOnlyExplainerSx = [proseSx, { color: Neutrals[600] }] as const;
const windowNoteSx = { marginBlockStart: 2 } as const;

export interface QuietHoursFormProps {
  enabled: boolean;
  from: string;
  to: string;
  invalid: QuietWindowFailure | null;
  summary: WindowSummary | null;
  readOnly: boolean;
  frozen: boolean;
  status?: React.ReactNode;
  notice?: React.ReactNode;
  windowNote?: React.ReactNode;
  onFieldBlur?: (field: QuietWindowField) => void;
  onEnabledChange: (enabled: boolean) => void;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
}

const clockOf = (hhmm: string, locale: string): string => {
  const parsed = hhmmToMinute(hhmm);
  return parsed.ok ? quietClock(parsed.value, locale) : hhmm;
};

const readOnlyValue = (t: TFunction, enabled: boolean, from: string, to: string, locale: string): string =>
  enabled
    ? t('closeTheLoop.quietHours.readOnlyOn', 'On · {{from}} → {{to}}', { from: clockOf(from, locale), to: clockOf(to, locale) })
    : t('closeTheLoop.quietHours.readOnlyOff', 'Off');

interface ReadOnlyQuietHoursProps {
  readonly enabled: boolean;
  readonly from: string;
  readonly to: string;
  readonly status?: React.ReactNode;
}

const ReadOnlyQuietHours: React.FC<ReadOnlyQuietHoursProps> = ({ enabled, from, to, status }) => {
  const { t } = useTranslation();
  const locale = useLocale();
  return (
    <SurfaceCard sx={formCardSx}>
      <Stack spacing={1} useFlexGap>
        <Typography variant="highlight" component="p" sx={labelSx}>
          {switchLabel(t)}
        </Typography>
        <Typography variant="body" component="p" sx={valueSx}>
          {readOnlyValue(t, enabled, from, to, locale)}
        </Typography>
        <Typography variant="label" component="p" sx={readOnlyExplainerSx}>
          {explainer(t, enabled)}
        </Typography>
      </Stack>
      {status}
    </SurfaceCard>
  );
};

const Slot: React.FC<{ children?: React.ReactNode; sx?: SxProps<Theme> }> = ({ children, sx }) => {
  if (children === undefined || children === null || children === false) return null;
  return <Box sx={sx}>{children}</Box>;
};

export const QuietHoursForm: React.FC<QuietHoursFormProps> = ({
  enabled,
  from,
  to,
  invalid,
  summary,
  readOnly,
  frozen,
  status,
  notice,
  windowNote,
  onFieldBlur,
  onEnabledChange,
  onFromChange,
  onToChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  if (readOnly) return <ReadOnlyQuietHours enabled={enabled} from={from} to={to} status={status} />;

  return (
    <SurfaceCard sx={formCardSx}>
      <SwitchInput
        layout="row"
        label={switchLabel(t)}
        helperText={explainer(t, enabled)}
        checked={enabled}
        readOnly={frozen}
        onChange={onEnabledChange}
      />
      <Slot>{status}</Slot>
      <Slot>{notice}</Slot>
      <Collapse in={enabled} unmountOnExit timeout={theme.transitions.duration.shorter} sx={collapseSx}>
        <QuietWindowFields
          from={from}
          to={to}
          invalid={invalid}
          summary={summary}
          readOnly={frozen}
          onFromChange={onFromChange}
          onToChange={onToChange}
          onBlur={onFieldBlur}
        />
        <Slot sx={windowNoteSx}>{windowNote}</Slot>
      </Collapse>
    </SurfaceCard>
  );
};
